/**
 * Mutators — the match settings that CHANGE the physics, and the resolved
 * struct the simulation actually reads.
 *
 * ===========================================================================
 * WHY THE MULTIPLIERS ARE NOT IN ./constants
 * ===========================================================================
 *
 * Every value in constants.ts names a primary source and carries a confidence
 * tag, because every value there is a claim about what Rocket League DOES. A
 * mutator multiplier is not that claim: it is a menu option, picked by whoever
 * set the match up, and no source has an opinion on it. Writing `1.5` next to
 * BALL_RADIUS with a `[1-src]` tag would be a lie about provenance, so the
 * multipliers live here and constants.ts keeps its sourcing rule intact. Every
 * BASE value below is imported from there; nothing in this file invents a
 * physics constant.
 *
 * ===========================================================================
 * TWO TYPES, TWO JOBS
 * ===========================================================================
 *
 * `MutatorConfig` is the MENU's shape — five enums, one per row, exactly what
 * src/game/settings.ts stores and what a player chose. It is part of the world
 * state: it clones, serializes and travels to a worker thread with everything
 * else, so a restored world plays by the same rules the original did.
 *
 * `ResolvedPhysics` is what the tick reads — plain numbers, resolved ONCE at
 * world construction. The hot path never sees an enum, never indexes a lookup
 * table and never multiplies by a mutator scale: `integrateBall` reads
 * `physics.gravity`, and under default mutators that field holds exactly the
 * GRAVITY constant.
 *
 * DEFAULTS ARE BIT-IDENTICAL, BY CONSTRUCTION. Every resolved number is written
 * so that a scale of 1 reproduces its base constant to the last bit: `x * 1` is
 * exact in IEEE 754, and the two offset forms (`BALL_REST_Z + (radius -
 * BALL_RADIUS)`) are exact for the default because `radius - BALL_RADIUS` is
 * then exactly 0. That is what lets the entire pre-existing numeric test suite
 * stand as the regression net for this change.
 *
 * ===========================================================================
 * GAME SPEED IS NOT IN ResolvedPhysics, AND THAT IS THE POINT
 * ===========================================================================
 *
 * The constants in ./constants are tuned for a fixed dt of 1/120 s and the
 * model is explicitly NOT dt-invariant — the ball's exponential drag, the flip
 * z-damp and the dodge torque all key off the tick RATE, and the boost, jump
 * and supersonic timers are all compared against absolute seconds. Implementing
 * slo-mo as `dt * 0.5` would therefore change the physics rather than the speed
 * of it.
 *
 * So game speed is a property of the DRIVER, not of the simulation: run fewer
 * or more fixed 120 Hz ticks per real second. The mechanism is `TickPacer` in
 * src/sim/loop.ts and `GAME_SPEED_SCALE` below is the table it takes its rate
 * from. The enum stays in `MutatorConfig` because that is one menu screen and
 * the world is where a match's settings live, but no physics function ever sees
 * it and `ResolvedPhysics` has no field for it.
 */

import {
  BALL_MASS,
  BALL_RADIUS,
  BALL_REST_Z,
  BALL_RESTITUTION,
  BOOST_CONSUMPTION_RATE,
  BOOST_MAX,
  BOOST_SPAWN_AMOUNT,
  GRAVITY,
  RECHARGE_BOOST_DELAY,
  RECHARGE_BOOST_PER_SECOND,
} from './constants';

// ---------------------------------------------------------------------------
// The menu's shape
// ---------------------------------------------------------------------------

export type BallSizeMutator = 'default' | 'small' | 'large' | 'gigantic';
export type BallBouncinessMutator = 'default' | 'low' | 'high' | 'super';
export type BoostMutator = 'default' | 'unlimited' | 'none' | 'recharge';
export type GameSpeedMutator = 'default' | 'slow' | 'fast';
export type GravityMutator = 'default' | 'low' | 'high' | 'reverse';

/**
 * One match's mutator settings. Re-exported by src/game/settings.ts, which is
 * where the menu binds to it — this module owns the definition so there is only
 * ever one copy of the enums and one copy of the multiplier tables.
 */
export interface MutatorConfig {
  ballSize: BallSizeMutator;
  ballBounciness: BallBouncinessMutator;
  boost: BoostMutator;
  gameSpeed: GameSpeedMutator;
  gravity: GravityMutator;
}

export const defaultMutators = (): MutatorConfig => ({
  ballSize: 'default',
  ballBounciness: 'default',
  boost: 'default',
  gameSpeed: 'default',
  gravity: 'default',
});

// ---------------------------------------------------------------------------
// Multiplier tables — game settings, not sourced physics
// ---------------------------------------------------------------------------

/** Multiplier on `BALL_RADIUS`, and so on everything derived from it: the rest
 *  height, the moment of inertia, the contact distance and the goal-line test. */
export const BALL_SIZE_SCALE: Record<BallSizeMutator, number> = {
  default: 1,
  small: 0.7,
  large: 1.5,
  gigantic: 2.5,
};

/** Multiplier on `BALL_RESTITUTION`, the ball-vs-world coefficient of
 *  restitution. `super` doubles it to 1.2, i.e. a bounce that gains energy —
 *  which is the mutator's whole point and is why nothing clamps this to 1. */
export const BALL_BOUNCINESS_SCALE: Record<BallBouncinessMutator, number> = {
  default: 1,
  low: 0.5,
  high: 1.5,
  super: 2,
};

/** Multiplier on wall-clock-to-sim-time; the tick rate itself never changes.
 *  Consumed by `TickPacer` in src/sim/loop.ts, never by the physics. */
export const GAME_SPEED_SCALE: Record<GameSpeedMutator, number> = {
  default: 1,
  slow: 0.5,
  fast: 1.5,
};

/** Multiplier on `GRAVITY`. Negative flips it, which is the point of `reverse`. */
export const GRAVITY_SCALE: Record<GravityMutator, number> = {
  default: 1,
  low: 0.5,
  high: 1.5,
  reverse: -0.65,
};

/**
 * Everything the boost mutator decides, per mode. Every number comes from
 * ./constants — including the recharge pair, which RocketSim already carries as
 * RECHARGE_BOOST_PER_SECOND / RECHARGE_BOOST_DELAY for exactly this mutator.
 *
 *   default    the game as shipped.
 *   unlimited  consumption is ZERO, so the tank never drops. It also spawns
 *              FULL rather than at BOOST_SPAWN_AMOUNT, which incidentally makes
 *              every pad uncollectable (a car at max leaves a pad standing) —
 *              the right behaviour for a mode where boost is free.
 *   none       the tank's maximum is 0, so a car spawns with nothing, no pad
 *              can give it anything, and `boostEnabled` refuses thrust even if
 *              something else writes a number into `car.boostAmount`.
 *   recharge   the tank refills at 10/s after 0.25 s without boosting.
 */
interface BoostResolution {
  readonly enabled: boolean;
  readonly max: number;
  readonly spawn: number;
  readonly consumption: number;
  readonly recharge: number;
  readonly rechargeDelay: number;
}

const BOOST_MODES: Record<BoostMutator, BoostResolution> = {
  default: {
    enabled: true,
    max: BOOST_MAX,
    spawn: BOOST_SPAWN_AMOUNT,
    consumption: BOOST_CONSUMPTION_RATE,
    recharge: 0,
    rechargeDelay: 0,
  },
  unlimited: {
    enabled: true,
    max: BOOST_MAX,
    spawn: BOOST_MAX,
    consumption: 0,
    recharge: 0,
    rechargeDelay: 0,
  },
  none: {
    enabled: false,
    max: 0,
    spawn: 0,
    consumption: BOOST_CONSUMPTION_RATE,
    recharge: 0,
    rechargeDelay: 0,
  },
  recharge: {
    enabled: true,
    max: BOOST_MAX,
    spawn: BOOST_SPAWN_AMOUNT,
    consumption: BOOST_CONSUMPTION_RATE,
    recharge: RECHARGE_BOOST_PER_SECOND,
    rechargeDelay: RECHARGE_BOOST_DELAY,
  },
};

// ---------------------------------------------------------------------------
// The resolved struct
// ---------------------------------------------------------------------------

/**
 * The mutator-dependent numbers, resolved once per world.
 *
 * Read by field on the hot path. It is created only by `resolvePhysics`, so
 * every instance shares one hidden class and a property load stays monomorphic
 * — which is the whole reason this is a flat struct of numbers rather than a
 * `MutatorConfig` threaded through every call and re-interpreted per tick.
 *
 * There is deliberately no `gameSpeed` here; see the file header.
 */
export interface ResolvedPhysics {
  /** Downward acceleration on the ball and on airborne cars, uu/s^2. Negative
   *  under the `reverse` mutator, which is what makes everything fall up. */
  readonly gravity: number;

  /** Geometric collision radius of the ball, uu. */
  readonly ballRadius: number;
  /** Height of the ball's centre at rest. The arena's 1.9 uu collision margin
   *  is a property of the MESH, not of the ball, so it does not scale: the rest
   *  height moves exactly as far as the radius does. */
  readonly ballRestZ: number;
  /** (2/5) m R^2 for the scaled ball. Unused by the bounce model, which mass
   *  and inertia cancel out of, and carried for the same reason BALL_INERTIA is
   *  carried in ./constants: so nobody re-derives it from a stale radius. */
  readonly ballInertia: number;
  /** Coefficient of restitution for ball-vs-world bounces. */
  readonly ballRestitution: number;

  /** False under the `none` mutator: no burst ever starts and no thrust is ever
   *  applied, whatever `car.boostAmount` happens to hold. */
  readonly boostEnabled: boolean;
  /** Tank capacity. Zero under `none`, which is also what stops pad pickups. */
  readonly boostMax: number;
  /** Tank level a car is placed with on kickoff and on respawn. */
  readonly boostSpawnAmount: number;
  /** Boost units consumed per second while a burst runs. Zero under
   *  `unlimited`, which is the whole of that mutator. */
  readonly boostConsumptionRate: number;
  /** Boost units regenerated per second once the delay has elapsed; 0 = off. */
  readonly boostRechargeRate: number;
  /** Seconds after the last burst before recharging starts. */
  readonly boostRechargeDelay: number;
}

/**
 * Resolve a `MutatorConfig` into the numbers the simulation reads.
 *
 * Pure and cheap; called once per world construction (and once per
 * deserialization), never per tick.
 */
export const resolvePhysics = (mutators: MutatorConfig): ResolvedPhysics => {
  const ballRadius = BALL_RADIUS * BALL_SIZE_SCALE[mutators.ballSize];
  const boost = BOOST_MODES[mutators.boost];

  return {
    gravity: GRAVITY * GRAVITY_SCALE[mutators.gravity],

    ballRadius,
    // Offset rather than scaled: see the field comment. At scale 1 the offset is
    // exactly 0, so this is exactly BALL_REST_Z.
    ballRestZ: BALL_REST_Z + (ballRadius - BALL_RADIUS),
    ballInertia: 0.4 * BALL_MASS * ballRadius * ballRadius,
    ballRestitution: BALL_RESTITUTION * BALL_BOUNCINESS_SCALE[mutators.ballBounciness],

    boostEnabled: boost.enabled,
    boostMax: boost.max,
    boostSpawnAmount: boost.spawn,
    boostConsumptionRate: boost.consumption,
    boostRechargeRate: boost.recharge,
    boostRechargeDelay: boost.rechargeDelay,
  };
};

/**
 * The unmutated game. Every physics function that takes a `ResolvedPhysics`
 * defaults to this one, so a caller that has never heard of mutators — a unit
 * test, ../bot's ball predictor — behaves exactly as it did before.
 *
 * Built through `resolvePhysics` rather than written out by hand so that it
 * cannot drift from the tables, and so that it shares its hidden class with
 * every mutated instance.
 */
export const DEFAULT_PHYSICS: ResolvedPhysics = resolvePhysics(defaultMutators());

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/** Pick `value` if it is a key of `table`, else `fallback`. `hasOwnProperty`
 *  rather than `in`, so `"toString"` is not a valid mutator. */
const oneOf = <K extends string>(
  value: unknown,
  table: Record<K, unknown>,
  fallback: K
): K =>
  typeof value === 'string' && Object.prototype.hasOwnProperty.call(table, value)
    ? (value as K)
    : fallback;

/**
 * A complete, valid `MutatorConfig` for literally any input, and always a fresh
 * object — the world must not alias a config its caller can still mutate.
 *
 * Total for the same reason `sanitizeSettings` is: this is the boundary a
 * deserialized state, a worker message or a localStorage payload crosses, and
 * an unknown mutator name must fall back to the default rather than resolve to
 * `undefined` and turn a multiplier into NaN.
 */
export const normalizeMutators = (value: unknown): MutatorConfig => {
  const base = defaultMutators();
  if (typeof value !== 'object' || value === null) return base;
  const v = value as Record<string, unknown>;
  return {
    ballSize: oneOf(v.ballSize, BALL_SIZE_SCALE, base.ballSize),
    ballBounciness: oneOf(v.ballBounciness, BALL_BOUNCINESS_SCALE, base.ballBounciness),
    boost: oneOf(v.boost, BOOST_MODES, base.boost),
    gameSpeed: oneOf(v.gameSpeed, GAME_SPEED_SCALE, base.gameSpeed),
    gravity: oneOf(v.gravity, GRAVITY_SCALE, base.gravity),
  };
};

/** True when every row is at its default. Cheap enough for an assertion or a
 *  HUD, and the one place "are these the shipped rules?" is answered. */
export const isDefaultMutators = (mutators: MutatorConfig): boolean => {
  const base = defaultMutators();
  return (
    mutators.ballSize === base.ballSize &&
    mutators.ballBounciness === base.ballBounciness &&
    mutators.boost === base.boost &&
    mutators.gameSpeed === base.gameSpeed &&
    mutators.gravity === base.gravity
  );
};

/** Ticks of simulation per second of wall clock, as a multiple of the fixed
 *  120 Hz rate. The driver's number; see the file header. */
export const gameSpeedScale = (mutators: MutatorConfig): number =>
  GAME_SPEED_SCALE[mutators.gameSpeed];
